import { LATE_WARN_STEPS, TIMEZONE, type HandoverKind, type ShiftState } from './constants';

/**
 * Live-mode domain logic (SPEC §1.1G): the handover moments of a day and
 * late-arrival state from check-ins. Pure functions shared by the
 * coordinator UI (live panel, Predaje), the API live payload and the pg-boss
 * late-warning evaluator.
 */

export interface LiveShift {
  id: string;
  postId: string;
  fromMin: number;
  toMin: number;
  volunteerId: string | null;
  state: ShiftState;
  /**
   * A `[x, 24:00]` shift whose person carries on at this post from 00:00 the
   * next day — one shift split at midnight — or the `[00:00, y]` half that
   * carries on from the day before. Only the server sees both days, so it says
   * (`markMidnightSplits`).
   */
  continuesNextDay?: boolean;
  continuesPrevDay?: boolean;
  /**
   * On a `[00:00, y]` half: the check-in record of its `[x, 24:00]` half the
   * day before, which is where the person's arrival was recorded
   * (`seenAcrossMidnight`). The server fills it in beside the marks.
   */
  prevHalfSeen?: ShiftSeen | null;
}

/** A shift's check-in record as the live screens read it: the minute of the newest arrival and of the newest check-out. */
export interface ShiftSeen {
  inAt: number | null;
  outAt: number | null;
}

/** The newest check-in row of each kind per shift, from rows ordered by when they were recorded. */
export function checkinSeen(
  rows: readonly { shiftId: string; kind: 'in' | 'out'; atMin: number }[],
): Map<string, ShiftSeen> {
  const seen = new Map<string, ShiftSeen>();
  for (const r of rows) {
    const entry = seen.get(r.shiftId) ?? { inAt: null, outAt: null };
    if (r.kind === 'in') entry.inAt = r.atMin;
    else entry.outAt = r.atMin;
    seen.set(r.shiftId, entry);
  }
  return seen;
}

/**
 * A shift's check-in record with midnight read through: the import splits a
 * shift that runs past midnight in two (IMPORT-2026.md) and the arrival lands
 * on the `[x, 24:00]` half, so the two halves are read as the one shift they
 * are. The `[00:00, y]` half counts the arrival at the first half, and a
 * check-out from the first half — before midnight or tidied up after it — as
 * its own: the person came and has left, as on a shift that was never split.
 * A record of its own after midnight stands. `inAt` and `outAt` may then be
 * minutes of the day before.
 */
export function seenAcrossMidnight(shift: Pick<LiveShift, 'prevHalfSeen'>, own: ShiftSeen): ShiftSeen {
  const prev = shift.prevHalfSeen;
  if (!prev || own.inAt !== null) return own;
  return { inAt: prev.inAt, outAt: own.outAt ?? prev.outAt };
}

/**
 * Mark the halves of shifts split at midnight — the same person at the same
 * post from 24:00 to 00:00 the next day — on one day's shifts, reading every
 * shift of the event. A shift that really ends at 24:00 or starts at 00:00,
 * with nobody or somebody else on the other side, is left unmarked.
 */
export function markMidnightSplits<S extends { postId: string; fromMin: number; toMin: number; volunteerId: string | null }>(
  day: readonly S[],
  all: readonly { id: string; postId: string; fromMin: number; toMin: number; volunteerId: string | null; date: string }[],
  date: string,
): (S & { continuesNextDay: boolean; continuesPrevDay: boolean; prevHalfId: string | null })[] {
  const dateBy = (by: number) => {
    const d = new Date(`${date}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + by);
    return d.toISOString().slice(0, 10);
  };
  const next = dateBy(1);
  const prev = dateBy(-1);
  const same = (a: { postId: string; volunteerId: string | null }, b: { postId: string; volunteerId: string | null }) =>
    a.postId === b.postId && a.volunteerId !== null && a.volunteerId === b.volunteerId;
  return day.map((s) => {
    const prevHalf = s.fromMin === 0 ? all.find((o) => o.date === prev && o.toMin === 1440 && same(s, o)) : undefined;
    return {
      ...s,
      continuesNextDay: s.toMin === 1440 && all.some((o) => o.date === next && o.fromMin === 0 && same(s, o)),
      continuesPrevDay: prevHalf !== undefined,
      prevHalfId: prevHalf?.id ?? null,
    };
  });
}

/**
 * One moment at one post where people arrive or leave — a handover between
 * the people leaving and the people arriving, a post opening, a post closing,
 * or somebody continuing into their own next shift.
 */
export interface HandoverMoment<S extends LiveShift = LiveShift> {
  id: string;
  kind: HandoverKind;
  postId: string;
  /** The first incoming shift's start; for a closing, the last outgoing end. */
  atMin: number;
  /** Who leaves: their shift ends here. Empty for an opening. */
  outs: S[];
  /** Who arrives: their shift starts here. Empty for a closing. */
  ins: S[];
  /**
   * How long the post is held by both sides — the latest outgoing end minus the
   * earliest incoming start. Above zero an overlap, below it a gap with nobody
   * there. Null for an opening or a closing.
   */
  lap: number | null;
  /**
   * Somebody else holds the post through this moment, so it is never empty
   * whatever the lap says. Always false for an opening or a closing.
   */
  covered: boolean;
}

/**
 * Somebody holds this place on race day: an assigned or done shift with a
 * person on it. An open slot and a proposal nobody accepted hold nothing — the
 * one spelling of it for handovers, their progress, live states and the
 * check-in buttons.
 */
export const holdsPlace = (s: { volunteerId: string | null; state: ShiftState }): boolean =>
  s.volunteerId !== null && (s.state === 'assigned' || s.state === 'done');

/**
 * A start this close to an end is the same moment at the post: the one person
 * leaves as the other arrives. Further apart, the post closes and opens again.
 */
export const HANDOVER_TOLERANCE = 30;

/** How long two people may overlap and still be one handing over to the other. */
export const HANDOVER_MAX_OVERLAP = 120;

/**
 * The handovers of a day, derived rather than stored (SPEC §3) — the only
 * spelling of them, read by Predaje and the live panel.
 *
 * Per post, each shift's end is paired with every other shift's start within
 * {@link HANDOVER_TOLERANCE}; the connected groups of those pairs are the
 * moments. People on both sides of a moment continue and drop out of it: with
 * nobody else changing it is a *nadaljevanje*, with one side left empty nobody
 * hands over (a partner leaves or joins), otherwise it is a *predaja* from
 * whoever leaves to whoever arrives — two people arriving together are one
 * predaja, never a handover from one of them to the other. An end or a start
 * with no pair is a *zaključek* or a *prevzem* only when no other staffed shift
 * holds the post at that minute; otherwise somebody stays and nothing changes
 * hands.
 *
 * Pairing, not chaining times, keeps two shifts that merely start near each
 * other apart. The halves of one person's shift split at midnight
 * (`continuesNextDay`, `continuesPrevDay`) neither close the post at 24:00 nor
 * open it at 00:00. Cancelled shifts take no part; an open slot does, as
 * nobody (PROSTO).
 */
export function handoverMoments<S extends LiveShift>(shifts: readonly S[]): HandoverMoment<S>[] {
  const out: HandoverMoment<S>[] = [];
  const byPost = new Map<string, S[]>();
  for (const s of shifts) {
    if (s.state === 'cancelled') continue;
    byPost.set(s.postId, [...(byPost.get(s.postId) ?? []), s]);
  }
  // A proposal nobody accepted holds nothing, though it still takes part as a place.
  const staffed = holdsPlace;
  const ids = (list: S[]) => list.map((s) => s.id).sort().join(',');

  for (const [postId, list] of byPost) {
    // The halves of one person's shift split at midnight neither close nor
    // open the post there; a shift that really ends at 24:00 or starts at 00:00 does.
    const ends = list.filter((s) => !(s.toMin === 1440 && s.continuesNextDay));
    const starts = list.filter((s) => !(s.fromMin === 0 && s.continuesPrevDay));
    const parent = new Map<string, string>();
    const find = (k: string): string => {
      let r = k;
      while (parent.get(r) !== r) r = parent.get(r)!;
      parent.set(k, r);
      return r;
    };
    const union = (a: string, b: string) => parent.set(find(a), find(b));
    const paired = new Set<string>();
    /**
     * `st` takes over from `e`: it starts within the tolerance of `e`'s end,
     * or earlier while `e` is on — an overlap of up to {@link HANDOVER_MAX_OVERLAP}
     * — and runs on after `e` leaves, when nobody arrives as `e` leaves. Two
     * people who start hours apart and leave apart are partners, not a handover.
     */
    const close = (e: S, st: S) => st.id !== e.id && Math.abs(st.fromMin - e.toMin) <= HANDOVER_TOLERANCE;
    const near = (e: S, st: S) =>
      close(e, st) ||
      (st.id !== e.id &&
        st.fromMin < e.toMin &&
        st.fromMin > e.fromMin &&
        st.toMin > e.toMin &&
        e.toMin - st.fromMin <= HANDOVER_MAX_OVERLAP &&
        // Somebody arriving as they leave is who they hand over to; a partner
        // who came earlier in staggered cover is not.
        !starts.some((x) => close(e, x)));
    for (const e of ends) {
      for (const st of starts) {
        if (!near(e, st)) continue;
        /*
         * A gap between two people that a third person's short shift fills —
         * they arrive as the first leaves and leave as the next arrives — is
         * two handovers through that person, not one past them. Left paired,
         * it would join both into one group and hide the person between.
         */
        const bridged =
          st.fromMin > e.toMin && list.some((x) => x.id !== e.id && x.id !== st.id && near(e, x) && near(x, st));
        if (bridged) continue;
        for (const k of [`e:${e.id}`, `s:${st.id}`]) if (!parent.has(k)) parent.set(k, k);
        union(`e:${e.id}`, `s:${st.id}`);
        paired.add(`e:${e.id}`).add(`s:${st.id}`);
      }
    }
    const shiftOf = new Map(list.map((s) => [s.id, s]));
    const joined = new Map<string, { outs: S[]; ins: S[] }>();
    for (const k of paired) {
      const g = joined.get(find(k)) ?? { outs: [], ins: [] };
      (k.startsWith('e:') ? g.outs : g.ins).push(shiftOf.get(k.slice(2))!);
      joined.set(find(k), g);
    }
    /*
     * A shift whose start and end both fall in one handover — a helper there
     * for the half hour around it — neither leaves nor arrives in it: it is
     * taken out of both sides, and counts as cover.
     */
    const groups = [...joined.values()].map((g) => {
      const through = g.outs.filter((o) => g.ins.includes(o));
      return through.length === 0
        ? g
        : { outs: g.outs.filter((o) => !through.includes(o)), ins: g.ins.filter((i) => !through.includes(i)) };
    });
    /** Staffed shifts other than `changing` hold the post through the whole of [from, to]. */
    const heldThrough = (from: number, to: number, changing: S[]) => {
      let reach = from;
      const cover = list
        .filter((o) => staffed(o) && !changing.includes(o) && o.toMin >= from)
        .sort((a, b) => a.fromMin - b.fromMin);
      for (const o of cover) {
        if (o.fromMin > reach) return false;
        reach = Math.max(reach, o.toMin);
        if (reach >= to) return true;
      }
      return false;
    };
    for (const g of groups) {
      if (g.ins.length === 0 || g.outs.length === 0) continue;
      const atMin = Math.min(...g.ins.map((s) => s.fromMin));
      const continuing = new Set(
        g.outs.filter(staffed).map((s) => s.volunteerId).filter((v) => g.ins.some((i) => staffed(i) && i.volunteerId === v)),
      );
      const outs = g.outs.filter((s) => !(staffed(s) && continuing.has(s.volunteerId)));
      const ins = g.ins.filter((s) => !(staffed(s) && continuing.has(s.volunteerId)));
      if (outs.length === 0 && ins.length === 0) {
        const lastOut = Math.max(...g.outs.map((s) => s.toMin));
        out.push({
          id: `nadaljevanje|${ids(g.outs)}|${ids(g.ins)}`,
          kind: 'nadaljevanje',
          postId,
          atMin,
          outs: g.outs,
          ins: g.ins,
          lap: lastOut - atMin,
          // A break in one person's day is covered when somebody else holds the post through it.
          covered: heldThrough(Math.min(atMin, lastOut), Math.max(atMin, lastOut), [...g.outs, ...g.ins]),
        });
      } else if (outs.length > 0 && ins.length > 0) {
        // Measured from the people who hold a place: an unfilled slot neither
        // stays nor arrives, and must not paint a gap as an overlap.
        const heldOuts = outs.filter(staffed);
        const heldIns = ins.filter(staffed);
        const at = Math.min(...(heldIns.length ? heldIns : ins).map((s) => s.fromMin));
        const lastOut = Math.max(...(heldOuts.length ? heldOuts : outs).map((s) => s.toMin));
        // Somebody not changing — a partner, or a person carrying on into
        // their next shift — is there from before the first leaves until after
        // the last arrives: the post is never empty.
        const from = Math.min(at, lastOut);
        const to = Math.max(at, lastOut, ...ins.map((s) => s.fromMin));
        const covered = heldThrough(from, to, [...outs, ...ins]);
        out.push({ id: `predaja|${ids(outs)}|${ids(ins)}`, kind: 'predaja', postId, atMin: at, outs, ins, lap: lastOut - at, covered });
      } else {
        /*
         * A partner leaves or joins as somebody carries on — no handover. But
         * the one carrying on may take a break in it, and with nobody holding
         * the post through that break it is as empty as any gap.
         */
        const breakOuts = g.outs.filter((s) => staffed(s) && continuing.has(s.volunteerId));
        const breakIns = g.ins.filter((s) => staffed(s) && continuing.has(s.volunteerId));
        if (breakOuts.length === 0 || breakIns.length === 0) continue;
        const left = Math.max(...breakOuts.map((s) => s.toMin));
        const back = Math.min(...breakIns.map((s) => s.fromMin));
        // Only the person on the break is not holding the post; a partner in
        // the same moment may well be.
        if (back > left && !heldThrough(left, back, [...breakOuts, ...breakIns])) {
          out.push({
            id: `nadaljevanje|${ids(breakOuts)}|${ids(breakIns)}`,
            kind: 'nadaljevanje',
            postId,
            atMin: back,
            outs: breakOuts,
            ins: breakIns,
            lap: left - back,
            covered: false,
          });
        }
      }
    }
    /** Somebody else holds the post across this minute. */
    // A half of a shift split at midnight holds the post across 24:00 or 00:00.
    const heldAt = (min: number, self: S) =>
      list.some(
        (o) =>
          o.id !== self.id &&
          staffed(o) &&
          (o.fromMin < min || (o.continuesPrevDay === true && o.fromMin === min)) &&
          (o.toMin > min || (o.continuesNextDay === true && o.toMin === min)),
      );
    // People who open or close a post together are one moment, as they are one handover.
    const cluster = (items: S[], at: (s: S) => number) => {
      const groups: S[][] = [];
      for (const s of [...items].sort((a, b) => at(a) - at(b))) {
        const last = groups.at(-1);
        if (last && at(s) - at(last[0]!) <= HANDOVER_TOLERANCE) last.push(s);
        else groups.push([s]);
      }
      return groups;
    };
    const closing = ends.filter((e) => !paired.has(`e:${e.id}`) && !heldAt(e.toMin, e));
    for (const g of cluster(closing, (s) => s.toMin)) {
      out.push({ id: `zaključek|${ids(g)}|${ids(g)}`, kind: 'zaključek', postId, atMin: Math.max(...g.map((s) => s.toMin)), outs: g, ins: [], lap: null, covered: false });
    }
    const opening = starts.filter((st) => !paired.has(`s:${st.id}`) && !heldAt(st.fromMin, st));
    for (const g of cluster(opening, (s) => s.fromMin)) {
      out.push({ id: `prevzem|${ids(g)}|${ids(g)}`, kind: 'prevzem', postId, atMin: g[0]!.fromMin, outs: [], ins: g, lap: null, covered: false });
    }
  }
  return out.sort((a, b) => a.atMin - b.atMin || (a.postId < b.postId ? -1 : a.postId > b.postId ? 1 : 0));
}

export interface HandoverProgress {
  /** People who were to arrive (or, for a closing, to leave) and did. */
  done: number;
  expected: number;
  /** Everyone did — the moment needs nothing more from anyone. */
  complete: boolean;
}

/**
 * How far a moment has got, read from check-ins alone (SPEC §1.1I): a predaja
 * or a prevzem is complete once everyone arriving has checked in, a zaključek
 * once everyone leaving has checked out. An external team checks in with its
 * own leader, never with us, so its places are not counted either way — a
 * team-only moment is complete once its time has come, and a team beside one of ours never
 * makes the handover read partly done before anybody is there. A slot with
 * nobody on it never arrives.
 */
export function handoverProgress<S extends LiveShift>(
  m: HandoverMoment<S>,
  seen: { arrived: (s: S) => boolean; left: (s: S) => boolean; external: (s: S) => boolean },
  nowMin: number,
): HandoverProgress {
  const all = m.kind === 'zaključek' ? m.outs : m.ins;
  const empty = (s: S) => !holdsPlace(s);
  // Nobody leaves from a place nobody held, so a closing does not wait on one.
  const people = all.filter((s) => !seen.external(s) && !(m.kind === 'zaključek' && empty(s)));
  const ok = (s: S) => !empty(s) && (m.kind === 'zaključek' ? seen.left(s) : seen.arrived(s));
  const done = people.filter(ok).length;
  // A moment only teams take part in has nobody to hear from: it has happened
  // once its time has come, and not before.
  const complete = all.length > 0 && (people.length === 0 ? nowMin >= m.atMin : done === people.length);
  return { done, expected: people.length, complete };
}

export type LiveStatus =
  | 'kasneje'
  | 'kmalu'
  | 'na'
  | 'ni'
  | 'zamuja'
  | 'brez'
  | 'odjavljen'
  | 'niodj'
  | 'prosto'
  | 'ekipa';

/** How far ahead a shift counts as coming up. */
export const LIVE_SOON_MINUTES = 45;
/** How long after its end a checked-in shift with no check-out still reads as on site. */
export const LIVE_CHECKOUT_GRACE = 30;

export function liveStatus(
  shift: { fromMin: number; toMin: number; state: ShiftState; volunteerId: string | null },
  seen: { inAt: number | null; outAt: number | null },
  nowMin: number,
  opts: { external?: boolean } = {},
): LiveStatus | null {
  // A proposal nobody accepted is nobody there, like an open slot.
  if (!holdsPlace(shift)) {
    return nowMin < shift.toMin && shift.fromMin - nowMin <= LIVE_SOON_MINUTES ? 'prosto' : null;
  }
  if (opts.external) {
    if (nowMin >= shift.toMin) return null;
    if (nowMin >= shift.fromMin) return 'ekipa';
    return shift.fromMin - nowMin <= LIVE_SOON_MINUTES ? 'kmalu' : 'kasneje';
  }
  if (shift.state === 'done' || seen.outAt !== null) return 'odjavljen';
  if (seen.inAt !== null) return nowMin > shift.toMin + LIVE_CHECKOUT_GRACE ? 'niodj' : 'na';
  if (shift.fromMin - nowMin > LIVE_SOON_MINUTES) return 'kasneje';
  if (nowMin < shift.fromMin) return 'kmalu';
  if (nowMin < shift.fromMin + LATE_WARN_STEPS[0]) return 'ni';
  if (nowMin < shift.toMin) return 'zamuja';
  return 'brez';
}

/** Worst first: the order a post's people are drawn in, and what a list sorts by. */
export const LIVE_RANK: readonly LiveStatus[] = [
  'prosto',
  'zamuja',
  'brez',
  'ni',
  'kmalu',
  'na',
  'ekipa',
  'niodj',
  'odjavljen',
  'kasneje',
];

/** How long after a shift ends it still says something about the post now. */
const LIVE_AFTER_END = 30;

export interface LivePart<S> {
  shift: S;
  status: LiveStatus;
}

/**
 * Where one shift stands once a person's back-to-back shifts at a post are
 * read as one stretch — the only spelling of it, for the pin, the post card,
 * the attention list and the check-in buttons alike.
 *
 * Once the next of the same person's shifts at the post has begun (it starts
 * within {@link HANDOVER_TOLERANCE} of the end of the one before), the two are
 * one stretch: the next counts the check-in to the one before, is late from
 * the start of the stretch, and the one before is `continued` — not drawn a
 * second time, and never "ni odjave". Before the next begins, each stands on
 * its own, and so does the next after a check-out from the one before. The
 * check-out that ends a stretch is the one before's too: `outAt` on a
 * `continued` shift is when the person left the post.
 */
export interface LiveShiftView {
  inAt: number | null;
  outAt: number | null;
  /** The minute lateness is counted from: the start of the stretch. */
  lateFrom: number;
  /** Carried on into the same person's next shift, which has begun. */
  continued: boolean;
}

export function liveShiftViews<S extends { id: string; fromMin: number; toMin: number; state: ShiftState; volunteerId: string | null }>(
  shifts: readonly S[],
  seenOf: (s: S) => { inAt: number | null; outAt: number | null },
  nowMin: number,
): Map<string, LiveShiftView> {
  const before = (s: S) =>
    shifts.find(
      (o) =>
        o !== s &&
        holdsPlace(o) &&
        o.volunteerId === s.volunteerId &&
        Math.abs(o.toMin - s.fromMin) <= HANDOVER_TOLERANCE &&
        o.fromMin < s.fromMin,
    );
  const views = new Map<string, LiveShiftView>();
  const carriedInto = new Map<string, S>();
  const viewOf = (s: S, depth = 0): LiveShiftView => {
    const known = views.get(s.id);
    if (known) return known;
    const seen = seenOf(s);
    const view: LiveShiftView = { ...seen, lateFrom: s.fromMin, continued: false };
    if (holdsPlace(s) && depth < 8) {
      const prev = before(s);
      if (prev && nowMin >= s.fromMin) {
        // A check-out ends the stretch, and so does a shift marked done: the
        // next shift is come to afresh.
        const pv = viewOf(prev, depth + 1);
        if (pv.outAt === null && prev.state !== 'done') {
          view.lateFrom = pv.lateFrom;
          if (view.inAt === null) view.inAt = pv.inAt;
        }
      }
      const next = shifts.find((o) => o !== s && holdsPlace(o) && before(o) === s);
      view.continued = next !== undefined && nowMin >= next.fromMin && seen.outAt === null && s.state !== 'done';
      if (view.continued) carriedInto.set(s.id, next!);
    }
    views.set(s.id, view);
    return view;
  };
  for (const s of shifts) viewOf(s);
  // Latest first, so the end of a stretch of three reaches its first shift.
  for (const s of [...shifts].sort((a, b) => b.fromMin - a.fromMin)) {
    const next = carriedInto.get(s.id);
    const left = next ? views.get(next.id)!.outAt : null;
    if (left !== null) views.get(s.id)!.outAt = left;
  }
  return views;
}

/** `liveStatus` of a shift as its stretch reads it (`liveShiftViews`). */
export function liveStatusIn(
  shift: { fromMin: number; toMin: number; state: ShiftState; volunteerId: string | null },
  view: LiveShiftView,
  nowMin: number,
  opts: { external?: boolean } = {},
): LiveStatus | null {
  // Carried on and still there; a no-show or somebody gone reads from the shift's own times.
  if (view.continued && view.inAt !== null && view.outAt === null) return 'na';
  return liveStatus({ ...shift, fromMin: nowMin >= shift.fromMin ? view.lateFrom : shift.fromMin }, view, nowMin, opts);
}

/**
 * The people who matter at a post right now, worst first (SPEC §1.2): whoever
 * is on it, whoever's shift starts within {@link LIVE_SOON_MINUTES}, a slot
 * with nobody on it, and whoever left or should have come in the last half
 * hour. A post with none of those but a shift later in the day is one
 * `kasneje` part; a post whose day is over has none. One person's
 * back-to-back shifts are one stretch (`liveShiftViews`).
 */
export function postLiveParts<S extends { id: string; fromMin: number; toMin: number; state: ShiftState; volunteerId: string | null }>(
  shifts: readonly S[],
  seenOf: (s: S) => { inAt: number | null; outAt: number | null },
  nowMin: number,
  isExternal: (s: S) => boolean,
): LivePart<S>[] {
  const parts: LivePart<S>[] = [];
  let later: LivePart<S> | null = null;
  const views = liveShiftViews(shifts, seenOf, nowMin);
  for (const shift of shifts) {
    if (shift.state === 'cancelled') continue;
    const view = views.get(shift.id)!;
    if (view.continued) continue;
    const status = liveStatusIn(shift, view, nowMin, { external: isExternal(shift) });
    if (status === null) continue;
    if (status === 'kasneje') {
      if (!later || shift.fromMin < later.shift.fromMin) later = { shift, status };
      continue;
    }
    const over = nowMin - shift.toMin;
    // Somebody who left early stays on the pin until their shift would have
    // ended: the post is theirs to fill until then.
    if (status === 'odjavljen' && nowMin - Math.max(view.outAt ?? shift.toMin, shift.toMin) > LIVE_AFTER_END) continue;
    if (status === 'brez' && over > LIVE_AFTER_END) continue;
    // "ni odjave" only begins half an hour after the end; it stays another hour.
    if (status === 'niodj' && over > LIVE_AFTER_END * 3) continue;
    parts.push({ shift, status });
  }
  parts.sort((a, b) => LIVE_RANK.indexOf(a.status) - LIVE_RANK.indexOf(b.status) || a.shift.fromMin - b.shift.fromMin);
  return parts.length ? parts : later ? [later] : [];
}

/**
 * Wall clock in Europe/Ljubljana for a given instant: ISO date + minutes from
 * midnight — the app's canonical time representation (SPEC §7).
 */
export function zonedNow(at: Date = new Date()): { isoDate: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return {
    isoDate: `${get('year')}-${get('month')}-${get('day')}`,
    // Intl renders midnight as "24" in some runtimes; normalise to 0
    minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')),
  };
}

/** How long before a shift starts and after it ends a marshal may check in or out. */
export const CHECKIN_MARGIN_MIN = 30;

/**
 * When a marshal may check in and out of their own shift: from
 * `CHECKIN_MARGIN_MIN` before it starts to the same after it ends, in
 * Europe/Ljubljana. A coordinator records a check-in at any time; this bounds
 * only the marshal's own page, and the server asks it too.
 *
 * Minutes run against the shift's own date, so the window of a `[x, 24:00]`
 * piece reaches past midnight into the next date and that of a `[00:00, y]`
 * piece opens on the previous one.
 */
export function checkinWindow(
  shift: { date: string; fromMin: number; toMin: number },
  now: { isoDate: string; minutes: number },
): { opensAt: number; closesAt: number; open: boolean } {
  const opensAt = shift.fromMin - CHECKIN_MARGIN_MIN;
  const closesAt = shift.toMin + CHECKIN_MARGIN_MIN;
  const days = dayNumber(now.isoDate) - dayNumber(shift.date);
  if (Math.abs(days) > 1) return { opensAt, closesAt, open: false };
  const at = days * 1440 + now.minutes;
  return { opensAt, closesAt, open: at >= opensAt && at <= closesAt };
}

function dayNumber(isoDate: string): number {
  return Math.round(Date.parse(`${isoDate}T00:00:00Z`) / 86_400_000);
}
